import { FC, FormEvent, useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BrandDialog from '../../components/BrandDialog';
import FormRow from '../../components/FormRow';
import MultiSelectChips, { ChipOption } from '../../components/MultiSelectChips';
import { ON_PRIMARY_ERROR_SX, ON_PRIMARY_FIELD_SX } from '../../components/fieldStyles';
import { UserContext } from '../..';
import { ROLE_ADMIN } from '../../constants';
import { colors, fontFamilies, fontSizes, fontWeights } from '../../theme/tokens';
import { fullName, useFilterOptions } from '../Home/useFilterOptions';
import DateCriterionFields from './DateCriterionFields';
import { fieldErrorsFrom, THROTTLED_TEXT } from './api';
import {
  EMPTY_CRITERIA,
  SearchCriteria,
  joinKeywords,
  splitKeywords,
} from './criteria';
import { searchResultsPathFor } from './paths';
import { useSearchCount } from './useSearchCount';

// XD screen 6 ("6. Recherche — Chercher un document" in DESIGN.md): the
// blue modal with the form on the left and, on the right, the live
// results panel ("Résultats de votre recherche", the big number,
// "documents disponibles", "Voir la liste", "Effacer").
//
// The criteria come in through the query string of `/search` (what is typed in
// the Home's search box, or Résultats' via "Modifier ma recherche") and
// go out towards `/search/results?…` (see criteria.ts and paths.ts). The
// form is local state while it is being filled.
//
// Decisions where the XD or the API give no data:
//   - "Émetteur": `GET /api/users/` is `requireAdmin`. A USER does not receive the
//     list and the field is free text with chips; the backend compares
//     `issuer` by equality, so what is typed must be what was archived.
//   - Right panel: white heading and the number in the strong orange, as the
//     artboard draws them. At its display size the number is large text,
//     where `#E76C4F` on the blue (3:1) meets WCAG AA.
//   - No result yet: the number shows a dimmed "—", not a spinner.
//   - Singular/plural: "0 document disponible", "1 document disponible",
//     "23 documents disponibles".
//   - The backend's 400s are shown under the field they name (api.ts);
//     a 429 shows "Trop de requêtes" and retries by itself.
//   - Mobile: the smartphone XD screen S7 (DESIGN.md, "Diseño móvil") — a
//     "‹ Retour" link over the form, the operators stacked above their date
//     fields, the chips above each dropdown with no ⊕, and no results
//     panel: the live count is embedded in the primary button ("Afficher
//     les 23 documents"). LTU resolved the label mismatches the two XDs
//     had (M-5) in favour of the desktop wording throughout: "Emission" /
//     "Archivage" (not the mobile artboard's "Date d'émission" / "Date
//     d'archivage"), and the secondary button is "Effacer" — it clears the
//     form like desktop's, not a second way to close (that is already
//     "‹ Retour").

type Props = {
  open: boolean;
  initial: SearchCriteria;
  /** Pre-search route for Résultats' "‹ Retour" (see paths.ts). */
  origin: string;
  onClose: () => void;
};

const LABEL_WIDTH = 110;

const SWITCH_SX = {
  '& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb': {
    backgroundColor: colors.white,
  },
  '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
    backgroundColor: colors.white,
    opacity: 0.5,
  },
} as const;

const availableText = (count: number): string =>
  count >= 2 ? 'documents disponibles' : 'document disponible';

// The S7 primary button carries the live count ("Afficher les 23 documents").
const showButtonText = (count: number | undefined): string => {
  if (count === undefined || count === 0) return 'Afficher les documents';
  if (count === 1) return 'Afficher le document';
  return `Afficher les ${count} documents`;
};

const SearchDialog: FC<Props> = ({ open, initial, origin, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const isAdmin = userContext?.mainUser?.role === ROLE_ADMIN;

  // "Mots clés" is stored as typed: the commas are the user's.
  const [keywordsText, setKeywordsText] = useState(joinKeywords(initial.keywords));
  const [form, setForm] = useState<SearchCriteria>(initial);

  const criteria = useMemo<SearchCriteria>(
    () => ({ ...form, keywords: splitKeywords(keywordsText) }),
    [form, keywordsText],
  );

  const { users, services } = useFilterOptions(isAdmin);

  const serviceOptions = useMemo<ChipOption[]>(
    () =>
      services
        .map((service) => ({ value: service.id, label: service.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [services],
  );

  const issuerOptions = useMemo<ChipOption[]>(
    () =>
      users
        .filter((user) => Boolean(user.id))
        .map((user) => ({ value: user.id as string, label: fullName(user) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [users],
  );

  const { count, isStale, error, throttled } = useSearchCount(criteria, open);
  const fieldErrors = error ? fieldErrorsFrom(error) : {};
  const blocked = Boolean(fieldErrors.issued || fieldErrors.archived);

  const update = (patch: Partial<SearchCriteria>) =>
    setForm((current) => ({ ...current, ...patch }));

  const clear = () => {
    setKeywordsText('');
    setForm(EMPTY_CRITERIA);
  };

  const showResults = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (blocked) return;
    navigate(searchResultsPathFor(criteria), { state: { origin } });
  };

  // --- Live results panel ------------------------------------------------

  const countText = count === undefined ? '—' : String(count);
  const statusText = throttled
    ? THROTTLED_TEXT
    : fieldErrors.form ?? undefined;

  const bigNumber = (
    <Typography
      component="p"
      data-testid="search-count"
      aria-live="polite"
      aria-busy={isStale}
      sx={{
        fontFamily: fontFamilies.sans,
        fontWeight: fontWeights.bold,
        fontSize: isMobile ? '2.25rem' : `${fontSizes.display / 16}rem`,
        lineHeight: 1,
        color: colors.accent,
        opacity: isStale ? 0.45 : 1,
        transition: theme.transitions.create('opacity'),
      }}
    >
      {countText}
    </Typography>
  );

  const seeListButton = (
    <Button
      type="submit"
      variant="contained"
      color="secondary"
      disabled={blocked}
      sx={{ minWidth: 160 }}
    >
      Voir la liste
    </Button>
  );

  const clearButton = (
    <Button variant="outlined" color="inherit" onClick={clear} sx={{ minWidth: 160 }}>
      Effacer
    </Button>
  );

  const desktopPanel = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        px: 2,
      }}
    >
      <Typography
        sx={{
          color: colors.white,
          fontWeight: fontWeights.bold,
          fontSize: '1rem',
        }}
      >
        Résultats de votre recherche
      </Typography>
      {bigNumber}
      <Typography sx={{ color: colors.white }}>
        {availableText(count ?? 0)}
      </Typography>
      {statusText && (
        <Typography role="status" sx={{ ...ON_PRIMARY_ERROR_SX, mt: 0 }}>
          {statusText}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
        {seeListButton}
        {clearButton}
      </Box>
    </Box>
  );

  return (
    <BrandDialog
      open={open}
      title="Chercher un document"
      onClose={onClose}
      onSubmit={showResults}
      maxWidth="xl"
    >
      <Box
        sx={(t) => ({
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(260px, 1fr)',
          columnGap: 4,
          [t.breakpoints.down('tablet')]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
          },
        })}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* S7: the sheet carries its own "‹ Retour", closing like the ✕. */}
          {isMobile && (
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                color: colors.accentLight,
                fontWeight: fontWeights.bold,
                fontSize: '0.8125rem',
              }}
            >
              <ChevronLeftIcon fontSize="small" />
              Retour
            </Box>
          )}

          <FormRow label="Mots clés" htmlFor="search-keywords" labelWidth={LABEL_WIDTH}>
            <TextField
              id="search-keywords"
              name="q"
              size="small"
              fullWidth
              autoFocus={!isMobile}
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder="Tapez les mots clés séparés par des virgules"
              sx={ON_PRIMARY_FIELD_SX}
            />
          </FormRow>

          <FormRow
            label="Emission"
            id="search-issued-label"
            labelWidth={LABEL_WIDTH}
            error={fieldErrors.issued}
          >
            <DateCriterionFields
              idPrefix="search-issued"
              label="Emission"
              labelledBy="search-issued-label"
              value={form.issued}
              onChange={(issued) => update({ issued })}
              error={fieldErrors.issued}
            />
          </FormRow>

          <FormRow
            label="Archivage"
            id="search-archived-label"
            labelWidth={LABEL_WIDTH}
            error={fieldErrors.archived}
          >
            <DateCriterionFields
              idPrefix="search-archived"
              label="Archivage"
              labelledBy="search-archived-label"
              value={form.archived}
              onChange={(archived) => update({ archived })}
              error={fieldErrors.archived}
            />
          </FormRow>

          <FormRow label="Service" id="search-services-label" alignTop labelWidth={LABEL_WIDTH}>
            <MultiSelectChips
              id="search-services"
              label="Service"
              outlined
              placeholder="Sélectionnez un service"
              options={serviceOptions}
              value={form.services}
              onChange={(services) => update({ services })}
              name="services"
              chipsAbove={isMobile}
              addOnSelect={isMobile}
            />
          </FormRow>

          <FormRow label="Émetteur" id="search-issuers-label" alignTop labelWidth={LABEL_WIDTH}>
            <MultiSelectChips
              outlined
              id="search-issuers"
              label="Émetteur"
              placeholder={
                isAdmin ? 'Sélectionnez un émetteur' : "Saisissez un émetteur"
              }
              options={issuerOptions}
              value={form.issuers}
              onChange={(issuers) => update({ issuers })}
              name="issuers"
              freeText={!isAdmin}
              chipsAbove={isMobile}
              addOnSelect={isMobile}
            />
          </FormRow>

          <FormRow label="Confidentiel" htmlFor="search-confidential" labelWidth={LABEL_WIDTH}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                id="search-confidential"
                name="confidential"
                color="secondary"
                checked={form.confidential}
                onChange={(e) => update({ confidential: e.target.checked })}
                sx={SWITCH_SX}
              />
              <Typography sx={{ fontSize: '0.875rem' }}>
                {form.confidential ? 'Oui' : 'Non'}
              </Typography>
            </Box>
          </FormRow>

          {/* S7: the count lives in the primary button; the secondary one
              clears the form, matching desktop's "Effacer" — "‹ Retour"
              above is already the way to close. */}
          {isMobile && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                mt: 1,
              }}
            >
              {statusText && (
                <Typography role="status" sx={{ ...ON_PRIMARY_ERROR_SX, mt: 0 }}>
                  {statusText}
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={blocked}
                aria-live="polite"
                aria-busy={isStale}
                sx={{ minWidth: 240, opacity: isStale ? 0.85 : 1 }}
              >
                {showButtonText(count)}
              </Button>
              {clearButton}
            </Box>
          )}
        </Box>

        {!isMobile && desktopPanel}
      </Box>
    </BrandDialog>
  );
};

export default SearchDialog;
